export type MaipoJsonDecodeErrorCode =
  | 'duplicate-member'
  | 'invalid-syntax'
  | 'invalid-utf8'
  | 'non-finite-number'
  | 'unpaired-surrogate'
  | 'input-limit'
  | 'depth-limit'
  | 'value-limit'
  | 'string-limit'
  | 'trailing-content';

export interface MaipoJsonDecodeLimits {
  maxBytes?: number;
  maxCodeUnits?: number;
  maxDepth?: number;
  maxValues?: number;
  maxStringCodeUnits?: number;
}

export const MAIPO_JSON_DECODE_LIMITS = Object.freeze({
  maxBytes: 256 * 1024 * 1024,
  maxCodeUnits: 256 * 1024 * 1024,
  maxDepth: 256,
  maxValues: 2_000_000,
  maxStringCodeUnits: 256 * 1024 * 1024,
});

type PathSegment = string | number;

function pathLabel(path: readonly PathSegment[]): string {
  return `$${path.map((segment) => typeof segment === 'number' ? `[${String(segment)}]` : `[${JSON.stringify(segment)}]`).join('')}`;
}

function lineAndColumn(text: string, offset: number): { line: number; column: number } {
  let line = 1;
  let column = 1;
  for (let index = 0; index < offset && index < text.length; index += 1) {
    const code = text.charCodeAt(index);
    if (code === 0x0d) {
      if (text.charCodeAt(index + 1) === 0x0a && index + 1 < offset) index += 1;
      line += 1;
      column = 1;
    } else if (code === 0x0a) {
      line += 1;
      column = 1;
    } else {
      column += 1;
    }
  }
  return { line, column };
}

export class MaipoJsonDecodeError extends SyntaxError {
  readonly path: string;
  readonly line: number;
  readonly column: number;
  readonly member?: string;

  constructor(
    readonly code: MaipoJsonDecodeErrorCode,
    detail: string,
    text: string,
    readonly offset: number,
    path: readonly PathSegment[],
    member?: string,
  ) {
    const location = lineAndColumn(text, offset);
    const at = pathLabel(path);
    super(`Maipo JSON ${code} at line ${String(location.line)}, column ${String(location.column)}, path ${at}: ${detail}`);
    this.name = 'MaipoJsonDecodeError';
    this.path = at;
    this.line = location.line;
    this.column = location.column;
    if (member !== undefined) this.member = member;
  }
}

function limit(value: number | undefined, fallback: number, name: string): number {
  const resolved = value ?? fallback;
  if (!Number.isSafeInteger(resolved) || resolved < 1) throw new RangeError(`${name} must be a positive safe integer.`);
  return resolved;
}

class StrictJsonParser {
  private index = 0;
  private values = 0;
  private stringCodeUnits = 0;
  private readonly path: PathSegment[] = [];
  private readonly maxDepth: number;
  private readonly maxValues: number;
  private readonly maxStringCodeUnits: number;

  constructor(private readonly text: string, options: MaipoJsonDecodeLimits) {
    const maxCodeUnits = limit(options.maxCodeUnits, MAIPO_JSON_DECODE_LIMITS.maxCodeUnits, 'maxCodeUnits');
    if (text.length > maxCodeUnits) throw new MaipoJsonDecodeError('input-limit', `Input has ${String(text.length)} UTF-16 code units; limit is ${String(maxCodeUnits)}.`, text, 0, []);
    this.maxDepth = limit(options.maxDepth, MAIPO_JSON_DECODE_LIMITS.maxDepth, 'maxDepth');
    this.maxValues = limit(options.maxValues, MAIPO_JSON_DECODE_LIMITS.maxValues, 'maxValues');
    this.maxStringCodeUnits = limit(options.maxStringCodeUnits, MAIPO_JSON_DECODE_LIMITS.maxStringCodeUnits, 'maxStringCodeUnits');
  }

  parse(): unknown {
    this.whitespace();
    const value = this.value(0);
    this.whitespace();
    if (this.index !== this.text.length) this.fail('trailing-content', 'Unexpected content follows the root value.');
    return value;
  }

  private fail(code: MaipoJsonDecodeErrorCode, detail: string, offset = this.index, member?: string): never {
    throw new MaipoJsonDecodeError(code, detail, this.text, offset, this.path, member);
  }

  private whitespace(): void {
    while (this.index < this.text.length) {
      const code = this.text.charCodeAt(this.index);
      if (code !== 0x20 && code !== 0x09 && code !== 0x0a && code !== 0x0d) return;
      this.index += 1;
    }
  }

  private value(depth: number): unknown {
    this.values += 1;
    if (this.values > this.maxValues) this.fail('value-limit', `Document contains more than ${String(this.maxValues)} values.`);
    const code = this.text.charCodeAt(this.index);
    if (code === 0x7b) return this.object(depth);
    if (code === 0x5b) return this.array(depth);
    if (code === 0x22) return this.string();
    if (code === 0x74) return this.literal('true', true);
    if (code === 0x66) return this.literal('false', false);
    if (code === 0x6e) return this.literal('null', null);
    if (code === 0x2d || (code >= 0x30 && code <= 0x39)) return this.number();
    this.fail('invalid-syntax', this.index >= this.text.length ? 'Expected a JSON value, but input ended.' : 'Expected a JSON value.');
  }

  private object(depth: number): Record<string, unknown> {
    if (depth >= this.maxDepth) this.fail('depth-limit', `Container nesting exceeds ${String(this.maxDepth)} levels.`);
    this.index += 1;
    this.whitespace();
    const result: Record<string, unknown> = {};
    const keys = new Set<string>();
    if (this.text.charCodeAt(this.index) === 0x7d) {
      this.index += 1;
      return {};
    }
    while (true) {
      if (this.text.charCodeAt(this.index) !== 0x22) this.fail('invalid-syntax', 'Expected an object member name.');
      const keyOffset = this.index;
      const key = this.string();
      if (keys.has(key)) this.fail('duplicate-member', `Duplicate object member ${JSON.stringify(key)}.`, keyOffset, key);
      keys.add(key);
      this.whitespace();
      if (this.text.charCodeAt(this.index) !== 0x3a) this.fail('invalid-syntax', 'Expected a colon after the object member name.');
      this.index += 1;
      this.whitespace();
      this.path.push(key);
      const child = this.value(depth + 1);
      this.path.pop();
      Object.defineProperty(result, key, { value: child, enumerable: true, configurable: true, writable: true });
      this.whitespace();
      const delimiter = this.text.charCodeAt(this.index);
      if (delimiter === 0x7d) {
        this.index += 1;
        return result;
      }
      if (delimiter !== 0x2c) this.fail('invalid-syntax', 'Expected a comma or closing brace.');
      this.index += 1;
      this.whitespace();
    }
  }

  private array(depth: number): unknown[] {
    if (depth >= this.maxDepth) this.fail('depth-limit', `Container nesting exceeds ${String(this.maxDepth)} levels.`);
    this.index += 1;
    this.whitespace();
    const result: unknown[] = [];
    if (this.text.charCodeAt(this.index) === 0x5d) {
      this.index += 1;
      return result;
    }
    while (true) {
      this.path.push(result.length);
      result.push(this.value(depth + 1));
      this.path.pop();
      this.whitespace();
      const delimiter = this.text.charCodeAt(this.index);
      if (delimiter === 0x5d) {
        this.index += 1;
        return result;
      }
      if (delimiter !== 0x2c) this.fail('invalid-syntax', 'Expected a comma or closing bracket.');
      this.index += 1;
      this.whitespace();
    }
  }

  private string(): string {
    const chunks: string[] = [];
    this.index += 1;
    let chunkStart = this.index;
    while (this.index < this.text.length) {
      const code = this.text.charCodeAt(this.index);
      if (code === 0x22) {
        this.appendRawChunk(chunks, chunkStart, this.index);
        this.index += 1;
        return chunks.join('');
      }
      if (code < 0x20) this.fail('invalid-syntax', 'Unescaped control character in string.');
      if (code >= 0xd800 && code <= 0xdbff) {
        const next = this.text.charCodeAt(this.index + 1);
        if (!(next >= 0xdc00 && next <= 0xdfff)) this.fail('unpaired-surrogate', 'Unpaired high surrogate in string.');
        this.index += 2;
        continue;
      }
      if (code >= 0xdc00 && code <= 0xdfff) this.fail('unpaired-surrogate', 'Unpaired low surrogate in string.');
      if (code !== 0x5c) {
        this.index += 1;
        continue;
      }

      this.appendRawChunk(chunks, chunkStart, this.index);
      this.index += 1;
      if (this.index >= this.text.length) this.fail('invalid-syntax', 'Unterminated escape sequence.');
      const escape = this.text[this.index]!;
      this.index += 1;
      const simple: Record<string, string> = { '"': '"', '\\': '\\', '/': '/', b: '\b', f: '\f', n: '\n', r: '\r', t: '\t' };
      if (escape in simple) {
        this.appendDecodedChunk(chunks, simple[escape]!);
        chunkStart = this.index;
        continue;
      }
      if (escape !== 'u') this.fail('invalid-syntax', `Unsupported escape sequence \\${escape}.`, this.index - 2);
      const first = this.hexCodeUnit();
      if (first >= 0xd800 && first <= 0xdbff) {
        if (this.text[this.index] !== '\\' || this.text[this.index + 1] !== 'u') this.fail('unpaired-surrogate', 'Escaped high surrogate is not followed by an escaped low surrogate.');
        this.index += 2;
        const second = this.hexCodeUnit();
        if (second < 0xdc00 || second > 0xdfff) this.fail('unpaired-surrogate', 'Escaped high surrogate is not followed by an escaped low surrogate.', this.index - 4);
        this.appendDecodedChunk(chunks, String.fromCharCode(first, second));
      } else if (first >= 0xdc00 && first <= 0xdfff) {
        this.fail('unpaired-surrogate', 'Escaped low surrogate has no preceding high surrogate.', this.index - 4);
      } else {
        this.appendDecodedChunk(chunks, String.fromCharCode(first));
      }
      chunkStart = this.index;
    }
    this.fail('invalid-syntax', 'Unterminated string.');
  }

  private addStringCodeUnits(count: number): void {
    this.stringCodeUnits += count;
    if (this.stringCodeUnits > this.maxStringCodeUnits) this.fail('string-limit', `Decoded strings exceed ${String(this.maxStringCodeUnits)} UTF-16 code units.`);
  }

  private appendRawChunk(chunks: string[], start: number, end: number): void {
    this.addStringCodeUnits(end - start);
    if (end > start) chunks.push(this.text.slice(start, end));
  }

  private appendDecodedChunk(chunks: string[], value: string): void {
    this.addStringCodeUnits(value.length);
    chunks.push(value);
  }

  private hexCodeUnit(): number {
    if (this.index + 4 > this.text.length) this.fail('invalid-syntax', 'Incomplete Unicode escape.');
    let value = 0;
    for (let count = 0; count < 4; count += 1) {
      const code = this.text.charCodeAt(this.index + count);
      const digit = code >= 0x30 && code <= 0x39 ? code - 0x30
        : code >= 0x41 && code <= 0x46 ? code - 0x41 + 10
          : code >= 0x61 && code <= 0x66 ? code - 0x61 + 10
            : -1;
      if (digit < 0) this.fail('invalid-syntax', 'Unicode escape contains a non-hexadecimal character.', this.index + count);
      value = value * 16 + digit;
    }
    this.index += 4;
    return value;
  }

  private literal(source: string, value: boolean | null): boolean | null {
    if (this.text.slice(this.index, this.index + source.length) !== source) this.fail('invalid-syntax', `Expected ${source}.`);
    this.index += source.length;
    return value;
  }

  private number(): number {
    const start = this.index;
    if (this.text.charCodeAt(this.index) === 0x2d) this.index += 1;
    if (this.text.charCodeAt(this.index) === 0x30) {
      this.index += 1;
      const next = this.text.charCodeAt(this.index);
      if (next >= 0x30 && next <= 0x39) this.fail('invalid-syntax', 'Leading zero in number.');
    } else {
      const first = this.text.charCodeAt(this.index);
      if (!(first >= 0x31 && first <= 0x39)) this.fail('invalid-syntax', 'Expected a digit in number.');
      while (this.index < this.text.length) {
        const code = this.text.charCodeAt(this.index);
        if (code < 0x30 || code > 0x39) break;
        this.index += 1;
      }
    }
    if (this.text.charCodeAt(this.index) === 0x2e) {
      this.index += 1;
      const first = this.text.charCodeAt(this.index);
      if (!(first >= 0x30 && first <= 0x39)) this.fail('invalid-syntax', 'Expected a fractional digit.');
      while (this.index < this.text.length) {
        const code = this.text.charCodeAt(this.index);
        if (code < 0x30 || code > 0x39) break;
        this.index += 1;
      }
    }
    const exponent = this.text.charCodeAt(this.index);
    if (exponent === 0x65 || exponent === 0x45) {
      this.index += 1;
      const sign = this.text.charCodeAt(this.index);
      if (sign === 0x2b || sign === 0x2d) this.index += 1;
      const first = this.text.charCodeAt(this.index);
      if (!(first >= 0x30 && first <= 0x39)) this.fail('invalid-syntax', 'Expected an exponent digit.');
      while (this.index < this.text.length) {
        const code = this.text.charCodeAt(this.index);
        if (code < 0x30 || code > 0x39) break;
        this.index += 1;
      }
    }
    const value = Number(this.text.slice(start, this.index));
    if (!Number.isFinite(value)) this.fail('non-finite-number', 'Number is outside finite IEEE-754 binary64.', start);
    return value;
  }
}

export function decodeMaipoJson(text: string, options: MaipoJsonDecodeLimits = {}): unknown {
  if (typeof text !== 'string') throw new TypeError('Maipo JSON input must be a string.');
  return new StrictJsonParser(text, options).parse();
}

export function decodeMaipoJsonBytes(bytes: Uint8Array, options: MaipoJsonDecodeLimits = {}): unknown {
  if (!(bytes instanceof Uint8Array)) throw new TypeError('Maipo JSON byte input must be a Uint8Array.');
  const maxBytes = limit(options.maxBytes, MAIPO_JSON_DECODE_LIMITS.maxBytes, 'maxBytes');
  if (bytes.byteLength > maxBytes) throw new MaipoJsonDecodeError('input-limit', `Input has ${String(bytes.byteLength)} bytes; limit is ${String(maxBytes)}.`, '', 0, []);
  let text: string;
  try {
    // Preserve a leading U+FEFF so the JSON grammar rejects an input BOM
    // instead of silently normalizing distinct byte sequences to one document.
    text = new TextDecoder('utf-8', { fatal: true, ignoreBOM: true }).decode(bytes);
  } catch {
    throw new MaipoJsonDecodeError('invalid-utf8', 'Input is not valid UTF-8.', '', 0, []);
  }
  return decodeMaipoJson(text, options);
}
